import React, { useState, useEffect } from 'react';
import { User } from '../api/entities.js';
import { Ticket } from '../api/entities.js';
import { Company } from '../api/entities.js';
import { Client } from '../api/entities.js';
import { 
  TrendingUp, 
  Users, 
  Ticket as TicketIcon, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  DollarSign, 
  Calendar,
  Zap,
  Activity,
  BarChart3,
  Sparkles,
  ArrowUpRight,
  Eye,
  Star,
  Building2,
  Brain,
  Target,
  Gauge,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Badge } from '../components/ui/badge.jsx';
import { Button } from '../components/ui/button.jsx';
import { Progress } from '../components/ui/progress.jsx';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';

// Corrigindo o import do DateRangeFilter
import DateRangeFilter from '../components/common/DateRangeFilter.jsx';
import ExportButton from '../components/common/ExportButton.jsx';

// Dashboards específicos por perfil
import InternalDashboard from '../components/dashboard/InternalDashboard.jsx';
import ClientDashboard from '../components/dashboard/ClientDashboard.jsx';

export default function Dashboard() {
  const [data, setData] = useState({
    tickets: [],
    companies: [],
    stats: {
      tickets: {
        total: 0,
        open: 0,
        overdue: 0,
        resolved: 0
      }
    },
    trends: {
      total: { change: 0, trend: 'stable' },
      open: { change: 0, trend: 'stable' },
      overdue: { change: 0, trend: 'stable' },
      resolved: { change: 0, trend: 'stable' }
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null,
  });
  const navigate = useNavigate();

  // Função para recarregar dados
  const refreshData = async () => {
    console.log('🔄 Refrescando dados do dashboard...');
    await loadData();
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadRealData = async () => {
    console.log('🔄 Carregando dados reais do dashboard...');
    
    let realTickets = [];
    let realCompanies = [];
    
    // Buscar tickets reais
    try {
      console.log('📋 Buscando tickets...');
      realTickets = await Ticket.list();
      console.log('✅ Tickets carregados:', realTickets?.length || 0);
    } catch (error) {
      console.warn('⚠️ Erro ao carregar tickets:', error.message);
      realTickets = [];
    }
    
    // Buscar empresas/clientes reais
    try {
      console.log('🏢 Buscando empresas...');
      
      // Tentar primeiro a tabela clients
      try {
        realCompanies = await Client.list();
        if (realCompanies && realCompanies.length > 0) {
          console.log('✅ Empresas carregadas via clients:', realCompanies.length);
        } else {
          throw new Error('Nenhum cliente encontrado');
        }
      } catch (clientError) {
        console.warn('⚠️ Tentativa via clients falhou, tentando companies:', clientError.message);
        
        // Fallback para tabela companies
        try {
          realCompanies = await Company.list();
          console.log('✅ Empresas carregadas via companies:', realCompanies?.length || 0);
        } catch (companyError) {
          console.warn('⚠️ Erro ao carregar companies:', companyError.message);
          realCompanies = [];
        }
      }
    } catch (error) {
      console.warn('⚠️ Erro geral ao carregar empresas:', error.message);
      realCompanies = [];
    }
    
    return { realTickets, realCompanies };
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const userData = await User.me();
      setUser(userData);
      
      // Carregar dados reais do banco
      const { realTickets, realCompanies } = await loadRealData();
      
      // Processar dados baseado no perfil do usuário
      if (userData.role === 'client') {
        const clientCompanyId = userData.company_id;
        
        // Filtrar tickets apenas da empresa do cliente
        const filteredTickets = realTickets.filter(t => 
          t.company_id === clientCompanyId || t.client_id === clientCompanyId
        );
        
        console.log('👤 Dados do cliente filtrados:', {
          tickets: filteredTickets.length,
          companyId: clientCompanyId
        });
        
        const statsWithTrends = calculateStats(filteredTickets);
        setData({
          tickets: filteredTickets,
          companies: realCompanies.filter(c => c.id === clientCompanyId),
          stats: statsWithTrends,
          trends: statsWithTrends.trends
        });
      } else {
        // Usuário interno - mostrar todos os dados
        console.log('👨‍💼 Dados completos para usuário interno:', {
          tickets: realTickets.length,
          companies: realCompanies.length
        });
        
        const statsWithTrends = calculateStats(realTickets);
        setData({
          tickets: realTickets,
          companies: realCompanies,
          stats: statsWithTrends,
          trends: statsWithTrends.trends
        });
      }
    } catch (error) {
      console.error('❌ Erro crítico ao carregar dados do dashboard:', error);
      
      // Fallback com dados básicos
      setData({
        tickets: [],
        companies: [],
        stats: {
          tickets: { total: 0, open: 0, overdue: 0, resolved: 0 }
        }
      });
    }
    setIsLoading(false);
  };

  const calculateTrends = (tickets) => {
    console.log('📈 Calculando tendências...');
    
    const safeTickets = Array.isArray(tickets) ? tickets : [];
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    
    // Tickets da última semana
    const lastWeekTickets = safeTickets.filter(t => {
      const createdDate = new Date(t.created_at);
      return createdDate >= sevenDaysAgo && createdDate <= now;
    });
    
    // Tickets da semana anterior
    const previousWeekTickets = safeTickets.filter(t => {
      const createdDate = new Date(t.created_at);
      return createdDate >= fourteenDaysAgo && createdDate < sevenDaysAgo;
    });
    
    const calculateChange = (current, previous) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return Math.round(((current - previous) / previous) * 100);
    };
    
    const getTrend = (change) => {
      if (change > 5) return 'up';
      if (change < -5) return 'down';
      return 'stable';
    };
    
    // Calcular estatísticas por semana
    const lastWeekStats = {
      total: lastWeekTickets.length,
      open: lastWeekTickets.filter(t => ['open', 'analysis', 'in_progress', 'reopened'].includes(t.status)).length,
      overdue: lastWeekTickets.filter(t => new Date(t.due_date || t.created_at) < now && ['open', 'analysis', 'in_progress'].includes(t.status)).length,
      resolved: lastWeekTickets.filter(t => ['resolved', 'closed'].includes(t.status)).length
    };
    
    const previousWeekStats = {
      total: previousWeekTickets.length,
      open: previousWeekTickets.filter(t => ['open', 'analysis', 'in_progress', 'reopened'].includes(t.status)).length,
      overdue: previousWeekTickets.filter(t => new Date(t.due_date || t.created_at) < sevenDaysAgo && ['open', 'analysis', 'in_progress'].includes(t.status)).length,
      resolved: previousWeekTickets.filter(t => ['resolved', 'closed'].includes(t.status)).length
    };
    
    const trends = {
      total: {
        change: calculateChange(lastWeekStats.total, previousWeekStats.total),
        trend: getTrend(calculateChange(lastWeekStats.total, previousWeekStats.total))
      },
      open: {
        change: calculateChange(lastWeekStats.open, previousWeekStats.open),
        trend: getTrend(calculateChange(lastWeekStats.open, previousWeekStats.open))
      },
      overdue: {
        change: calculateChange(lastWeekStats.overdue, previousWeekStats.overdue),
        trend: getTrend(calculateChange(lastWeekStats.overdue, previousWeekStats.overdue))
      },
      resolved: {
        change: calculateChange(lastWeekStats.resolved, previousWeekStats.resolved),
        trend: getTrend(calculateChange(lastWeekStats.resolved, previousWeekStats.resolved))
      }
    };
    
    console.log('📊 Tendências calculadas:', trends);
    return trends;
  };

  const calculateStats = (tickets) => {
    console.log('📊 Calculando estatísticas:', { tickets: tickets?.length });
    
    // Garantir que tickets seja array
    const safeTickets = Array.isArray(tickets) ? tickets : [];
    
    // Calcular data limite para tickets vencidos (baseado em created_at ou due_date)
    const now = new Date();
    
    // Função para verificar se ticket está vencido
    const isOverdue = (ticket) => {
      if (ticket.due_date) {
        return new Date(ticket.due_date) < now;
      }
      // Se não tem due_date, considerar vencido se criado há mais de 7 dias e ainda aberto
      if (ticket.created_at && ['open', 'analysis', 'in_progress'].includes(ticket.status)) {
        const createdDate = new Date(ticket.created_at);
        const daysDiff = (now - createdDate) / (1000 * 60 * 60 * 24);
        return daysDiff > 7; // 7 dias como SLA padrão
      }
      return false;
    };
    
    const ticketStats = {
      total: safeTickets.length,
      open: safeTickets.filter(t => ['open', 'analysis', 'in_progress', 'reopened'].includes(t.status)).length,
      overdue: safeTickets.filter(isOverdue).length,
      resolved: safeTickets.filter(t => ['resolved', 'closed'].includes(t.status)).length
    };
    
    // Calcular tendências
    const trends = calculateTrends(safeTickets);
    
    console.log('📈 Estatísticas calculadas:', { ticketStats, trends });
    
    return {
      tickets: ticketStats,
      trends: trends
    };
  };

  if (!user) return null;

  // Stats modernos com gradientes
  const modernStats = [
    {
      id: 'total_tickets',
      title: 'Total Chamados',
      value: data.stats.tickets.total,
      change: data.trends?.total?.change !== undefined 
        ? `${data.trends.total.change > 0 ? '+' : ''}${data.trends.total.change}%`
        : '0%',
      trend: data.trends?.total?.trend || 'stable',
      icon: TicketIcon,
      gradient: 'from-electric-400 to-electric-600',
      description: 'vs semana anterior'
    },
    {
      id: 'active_tickets',
      title: 'Chamados Ativos',
      value: data.stats.tickets.open,
      change: data.trends?.open?.change !== undefined 
        ? `${data.trends.open.change > 0 ? '+' : ''}${data.trends.open.change}%`
        : '0%',
      trend: data.trends?.open?.trend || 'stable',
      icon: Activity,
      gradient: 'from-magic-400 to-magic-600',
      description: 'vs semana anterior'
    },
    {
      id: 'overdue_tickets',
      title: 'Urgentes',
      value: data.stats.tickets.overdue,
      change: data.trends?.overdue?.change !== undefined 
        ? `${data.trends.overdue.change > 0 ? '+' : ''}${data.trends.overdue.change}%`
        : '0%',
      trend: data.trends?.overdue?.trend || 'stable',
      icon: AlertTriangle,
      gradient: 'from-red-400 to-red-600',
      description: 'vs semana anterior'
    },
    {
      id: 'resolved_tickets',
      title: 'Resolvidos',
      value: data.stats.tickets.resolved,
      change: data.trends?.resolved?.change !== undefined 
        ? `${data.trends.resolved.change > 0 ? '+' : ''}${data.trends.resolved.change}%`
        : '0%',
      trend: data.trends?.resolved?.trend || 'stable',
      icon: CheckCircle,
      gradient: 'from-neon-400 to-neon-600',
      description: 'vs semana anterior'
    }
  ];

  const formatTime = () => {
    return new Date().toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header moderno */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <h1 className="text-4xl font-bold gradient-text flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-electric flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              Neural Dashboard
            </h1>
            <div className="flex items-center gap-4 text-muted-foreground">
              <span>Bem-vindo de volta, <span className="text-electric-500 font-semibold">{user?.name || user?.email}</span></span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-neon-500 rounded-full animate-pulse"></div>
                <span className="text-sm">Sistema Neural Ativo</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <ExportButton
              data={data.tickets}
              filename="dashboard_tickets"
              title="Relatório do Dashboard - Tickets"
              type="tickets"
              variant="outline"
              className="glass-card hover:glow-neon transition-all duration-300"
            />
            <Button 
              className="bg-gradient-electric hover:glow-electric transition-all duration-300"
              onClick={() => navigate(createPageUrl('Reports'))}
            >
              <Eye className="w-4 h-4 mr-2" />
              Ver Relatórios
            </Button>
            <Button 
              variant="outline" 
              className="glass-card hover:glow-magic transition-all duration-300"
              onClick={refreshData}
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? 'Carregando...' : 'Atualizar'}
            </Button>
          </div>
        </div>

        {/* AI Insights */}
        <div className="glass-card p-6 border-electric-500/20">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-gradient-magic flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-lg font-semibold gradient-text">Insights de IA</h3>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={refreshData}
              disabled={isLoading}
              className="text-xs"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Taxa de Resolução</p>
              <p className="text-2xl font-bold text-electric-500">
                {data.stats.tickets.total > 0 
                  ? `${Math.round((data.stats.tickets.resolved / data.stats.tickets.total) * 100)}%`
                  : '0%'
                }
              </p>
              <p className="text-xs text-neon-500">
                {data.stats.tickets.resolved} de {data.stats.tickets.total} tickets
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Tickets Ativos</p>
              <p className="text-2xl font-bold text-magic-500">{data.stats.tickets.open}</p>
              <p className="text-xs text-sunset-500">
                {data.stats.tickets.overdue > 0 
                  ? `${data.stats.tickets.overdue} vencidos`
                  : 'Todos no prazo'
                }
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Empresas Ativas</p>
              <p className="text-2xl font-bold text-neon-500">{data.companies?.length || 0}</p>
              <p className="text-xs text-electric-500">
                {data.tickets?.length > 0 ? 'Com atividade' : 'Sistema ativo'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards Modernos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {modernStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card 
              key={stat.id}
              className={`modern-card hover:glow transition-all duration-500 animate-fade-in cursor-pointer ${
                hoveredCard === stat.id ? 'scale-105' : ''
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
              onMouseEnter={() => setHoveredCard(stat.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${stat.gradient} flex items-center justify-center`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-3xl font-bold gradient-text">
                    {stat.value}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`flex items-center gap-1 text-sm ${
                        stat.trend === 'up' ? 'text-neon-500' : 
                        stat.trend === 'down' ? 'text-red-500' : 'text-yellow-500'
                      }`}>
                        {stat.trend === 'up' && <ArrowUpRight className="w-3 h-3" />}
                        {stat.trend === 'down' && <ArrowUpRight className="w-3 h-3 rotate-90" />}
                        {stat.trend === 'stable' && <div className="w-3 h-0.5 bg-yellow-500 rounded"></div>}
                        {stat.change}
                      </div>
                    </div>
                    <Badge variant="secondary" className="glass-card">
                      {stat.description}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Dashboard Específico */}
      <div className="animate-slide-up">
        {user.role === 'client' ? (
          <ClientDashboard 
            data={data} 
            isLoading={isLoading} 
            className="space-y-6"
          />
        ) : (
          <InternalDashboard 
            data={data} 
            isLoading={isLoading}
            className="space-y-6"
          />
        )}
      </div>

      {/* Footer with system info */}
      <div className="mt-12 p-6 glass-card border-white/10">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-neon-500 rounded-full animate-pulse"></div>
              <span>Sistema Neural v2.0.1</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-3 h-3" />
              <span>99.9% Uptime</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span>Última atualização:</span>
            <span className="text-electric-500">
              {formatTime()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
